import { invoke } from '@tauri-apps/api/core';
import { TASK_STATUSES, statusPatch } from '../../extensions/bundled/task-manager/taskStatus';
import { subtasksFromInput } from '../../extensions/bundled/task-manager/subtasks';
import { mcpError } from '../errors';
import { refuseUnwritableScope } from '../writeScope';
import { scopeKeyFor } from './boards';

export const EXTENSION_ID = 'task-manager';

export const PRIORITIES = ['low', 'medium', 'high'];

export const VIEW_MODES = ['kanban', 'list'];

export const MAX_LISTS_ON_CREATE = 20;

/**
 * Everything a write needs before it touches the store: the resolved scope key,
 * or the refusal. Rule 3 and rule 4 of the header both live here, so no handler
 * can forget one.
 */
export function targetScope(wire) {
  const refusal = refuseUnwritableScope(wire);
  if (refusal) return { error: refusal };
  return { scopeKey: scopeKeyFor(wire) };
}

/** Every record of one board, in the flat `record_json` shape. */
export async function boardRows(scopeKey, boardId) {
  const rows = await invoke('extension_records_list', {
    workspaceId: scopeKey,
    extensionId: EXTENSION_ID,
    recordType: null,
    scopeId: String(boardId),
  });
  return rows || [];
}

/**
 * `id -> rev` for one board. Scoped, which is the whole reason
 * `extension_records_list_meta` gained a `scope_id` in this tranche: without it
 * this call returns the metadata of every task in every board of the scope.
 */
export async function revsFor(scopeKey, boardId) {
  const rows = await invoke('extension_records_list_meta', {
    workspaceId: scopeKey,
    extensionId: EXTENSION_ID,
    recordType: null,
    scopeId: String(boardId),
  });
  return new Map((rows || []).map((r) => [r.id, r.rev ?? null]));
}

/**
 * Rust answers with `conflict:<rev>` or `too_large:<bytes>:<cap>` and nothing
 * else — a fixed vocabulary, so no underlying error text is ever relayed to a
 * model. Deliberately NOT `documents.js`'s mapper: that emits
 * `document_too_large`, which would send a model looking for a document.
 */
export function fromBoardRustError(err) {
  const text = typeof err === 'string' ? err : '';
  if (text.startsWith('conflict:')) {
    return mcpError(
      'conflict',
      'That record changed since you read it. Read the board again and retry with the new rev.',
      { rev: text.slice('conflict:'.length) || null }
    );
  }
  if (text.startsWith('too_large:')) {
    const [, bytes, cap] = text.split(':');
    return mcpError(
      'record_too_large',
      'That record is too large to sync to the user\'s other devices, so NOTHING was written. Retrying will not help — shorten the text, or split the work across more tasks.',
      { bytes: Number(bytes) || null, cap: Number(cap) || null }
    );
  }
  return mcpError('write_failed', 'The write could not be completed.');
}

/** One CAS write. `{ rev }` or `{ error }` — it never throws for a store error. */
export async function casWrite(scopeKey, record, expectedRev) {
  try {
    const rev = await invoke('extension_records_cas_upsert', {
      workspaceId: scopeKey,
      extensionId: EXTENSION_ID,
      record,
      expectedRev: expectedRev ?? null,
    });
    return { rev };
  } catch (err) {
    return { error: fromBoardRustError(err) };
  }
}

export const findBoard = (rows, boardId) =>
  rows.find((r) => r.record_type === 'board' && r.id === String(boardId)) || null;

export const findGroup = (rows, listId) =>
  rows.find((r) => r.record_type === 'group' && r.id === String(listId)) || null;

export const findTask = (rows, taskId) =>
  rows.find((r) => r.record_type === 'task' && r.id === String(taskId)) || null;

/**
 * Every label id must be in the BOARD's own palette. Writing an unknown id
 * produces a chip that renders as nothing, and creating a label is out of scope
 * for MCP entirely — so an unknown id is a refusal, not a silent drop.
 */
export function checkLabels(boardRow, labels) {
  if (labels === undefined) return null;
  if (!Array.isArray(labels)) {
    return mcpError('invalid_argument', 'labels must be an array of label ids.', {
      field: 'labels',
    });
  }
  const palette = new Set((boardRow.labels || []).map((l) => String(l?.id)));
  const unknown = labels.map(String).filter((id) => !palette.has(id));
  if (unknown.length) {
    return mcpError(
      'invalid_argument',
      'Those label ids are not in this board\'s palette. Use the ids from board_read; new labels cannot be created over MCP.',
      { field: 'labels', unknown }
    );
  }
  return null;
}

export const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export function checkDueDate(dueDate) {
  if (dueDate === undefined || dueDate === null) return null;
  const text = String(dueDate);
  if (!DATE_ONLY.test(text) || Number.isNaN(Date.parse(text))) {
    return mcpError('invalid_argument', 'due_date must be a calendar date, as YYYY-MM-DD.', {
      field: 'due_date',
    });
  }
  return null;
}

/**
 * A task's status (card ef074711): todo, in_progress, done or dismissed. It
 * decides `done` (done and dismissed close the task), so a `done` that says the
 * opposite in the same call is refused rather than silently overridden.
 */
export function checkStatus(status, done) {
  if (status === undefined) return null;
  if (!TASK_STATUSES.includes(status)) {
    return mcpError('invalid_argument', 'status must be todo, in_progress, done or dismissed.', { field: 'status' });
  }
  if (done !== undefined && !!done !== statusPatch(status).done) {
    return mcpError('invalid_argument', `done=${!!done} contradicts status=${status}; send one of them.`, { field: 'done' });
  }
  return null;
}

export function checkPriority(priority) {
  if (priority === undefined) return null;
  if (!PRIORITIES.includes(priority)) {
    return mcpError('invalid_argument', 'priority must be low, medium or high.', {
      field: 'priority',
    });
  }
  return null;
}

/**
 * A position must be a whole number inside the list it is going into. Past the
 * end is refused rather than clamped: the app's positions are dense counts, and
 * silently landing somewhere other than where the model asked is the kind of
 * "success" that is worse than a refusal.
 */
export function checkPosition(position, count, field = 'position') {
  if (position === undefined) return null;
  if (!Number.isInteger(position) || position < 0) {
    return mcpError('invalid_argument', 'position must be a whole number, 0 or greater.', { field });
  }
  if (position > count) {
    return mcpError(
      'invalid_argument',
      'That position is past the end. Use board_read to see how many items the list holds.',
      { field, max: count }
    );
  }
  return null;
}

/**
 * `subtasks` as sent → the list to store, or an `invalid_argument` refusal.
 * The list REPLACES the task's checklist; `existing` is the task's own list,
 * so an id the task does not have is refused (subtasks.js holds the limits).
 */
export function checkSubtasks(subtasks, existing) {
  if (subtasks === undefined) return { subtasks: undefined };
  const out = subtasksFromInput(subtasks, existing);
  if (out.error) return { error: mcpError('invalid_argument', out.error.message, { field: out.error.field }) };
  return out;
}

export function requireRev(expectedRev) {
  if (expectedRev) return null;
  return mcpError(
    'expected_rev_required',
    'Changing something a person may have typed requires expected_rev from a preceding board_read.'
  );
}
